import { UserPreferences } from './supabase';

/**
 * Formats a time string (HH:mm) according to the user's preference (24h or 12h).
 * @param timeStr Time string in HH:mm format (e.g., "14:30")
 * @param preferences User preferences containing time_format
 * @returns Formatted time string (e.g., "14:30" or "2:30 PM")
 */
export function formatTime(timeStr: string, preferences: UserPreferences | null): string {
  if (!timeStr) return '';
  
  // Default to 24h if no preference
  const format = preferences?.time_format || '24h';
  
  if (format === '24h') {
    return timeStr;
  }

  // Convert "HH:mm" to 12h format
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Formats a date or ISO string to a localized date string, considering the user's timezone.
 * @param dateStr Date string or ISO string
 * @param language Language code (e.g., "en", "lv")
 * @param preferences User preferences containing timezone
 * @returns Formatted date string
 */
export function formatDate(dateStr: string, language: string, preferences?: UserPreferences | null, forceShowYear?: boolean, showTime?: boolean): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    
    // Determine if we should show the year
    const targetTimezone = preferences?.timezone || undefined;
    const isCurrentYear = (() => {
      const now = new Date();
      if (!targetTimezone) return date.getFullYear() === now.getFullYear();
      
      const format = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: targetTimezone });
      return format.format(date) === format.format(now);
    })();

    return new Intl.DateTimeFormat(language, {
      day: '2-digit',
      month: 'short',
      year: (forceShowYear || !isCurrentYear) ? 'numeric' : undefined,
      hour: showTime ? '2-digit' : undefined,
      minute: showTime ? '2-digit' : undefined,
      hour12: showTime ? (preferences?.time_format === '12h') : undefined,
      timeZone: targetTimezone
    }).format(date);
  } catch (e) {
    return dateStr;
  }
}

/**
 * Formats a date or ISO string to a localized date and time string.
 * @param dateStr Date string or ISO string
 * @param language Language code (e.g., "en", "lv")
 * @param preferences User preferences containing timezone and time_format
 * @returns Formatted date and time string
 */
export function formatDateTime(dateStr: string, language: string, preferences?: UserPreferences | null): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    const timeFormat = preferences?.time_format || '24h';
    
    return new Intl.DateTimeFormat(language, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: timeFormat === '12h',
      timeZone: preferences?.timezone || undefined
    }).format(date);
  } catch (e) {
    return dateStr;
  }
}

/**
 * Gets the time part (HH:mm) from an ISO string in a specific timezone.
 */
export function getTimeInTimezone(dateStr: string, timezone?: string): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone || undefined
    }).format(date);
  } catch (e) {
    return '';
  }
}

/**
 * Gets the date part (YYYY-MM-DD) from an ISO string in a specific timezone.
 */
export function getDateInTimezone(dateStr: string, timezone?: string): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    
    const parts = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone || undefined
    }).formatToParts(date);
    
    const y = parts.find(p => p.type === 'year')?.value;
    const m = parts.find(p => p.type === 'month')?.value;
    const d = parts.find(p => p.type === 'day')?.value;
    
    return `${y}-${m}-${d}`;
  } catch (e) {
    return dateStr.split('T')[0];
  }
}

/**
 * Converts a date and time in a specific timezone to a UTC ISO string.
 * @param dateStr Date string (YYYY-MM-DD)
 * @param timeStr Time string (HH:mm)
 * @param timezone Timezone name (e.g., "Europe/Riga")
 * @returns UTC ISO string
 */
export function convertToUTC(dateStr: string, timeStr: string, timezone?: string): string {
  if (!dateStr) return '';
  const time = timeStr || '12:00';
  
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const targetTimezone = timezone || browserTimezone;

    // We want to find a UTC date such that formatting it in 'targetTimezone' 
    // results in the desired dateStr and timeStr.
    
    // Step 1: Create a date object interpreting inputs as LOCAL if targetTimezone matches browserTimezone
    if (targetTimezone === browserTimezone) {
      const [y, m, d] = dateStr.split(/[-/]/).map(Number);
      const [h, min] = time.split(':').map(Number);
      const dtLocal = new Date(y, m - 1, d, h, min);
      return dtLocal.toISOString();
    }

    // Step 2: For other timezones, we use an iterative approach to find the correct offset
    // or use the heuristic: (UTC input) - (local offset in that timezone)
    const utcBase = new Date(`${dateStr}T${time}:00Z`);
    const dateInTz = new Date(utcBase.toLocaleString('en-US', { timeZone: targetTimezone }));
    const offset = utcBase.getTime() - dateInTz.getTime();
    
    return new Date(utcBase.getTime() + offset).toISOString();
  } catch (e) {
    return new Date(`${dateStr}T${time}`).toISOString();
  }
}
